import type { SalonRow } from '@manaro/contracts'

type Salon = NonNullable<SalonRow['salon']>
type Owner = SalonRow['user']

export type ContactKind = 'phone' | 'telegram' | 'viber' | 'whatsapp' | 'facebook' | 'email' | 'instagram' | 'website'

export interface ContactLink {
  kind: ContactKind
  /** What the support agent reads: the number, @handle or address itself. */
  label: string
  href: string
  /** The salon marked this way as convenient in its profile. */
  preferred: boolean
  /** Built from the phone number: the chat opens only if that number uses the messenger. */
  byPhone: boolean
}

export const CONTACT_NAMES: Record<ContactKind, string> = {
  phone: 'Телефон',
  telegram: 'Telegram',
  viber: 'Viber',
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  email: 'Почта',
  instagram: 'Instagram',
  website: 'Сайт',
}

const MIN_PHONE_DIGITS = 9
const UKRAINE = '38'
const UKRAINE_LOCAL_DIGITS = 10
const HANDLE = /^@?([a-z0-9_.]{3,64})$/i

const digitsOf = (value: string): string => value.replace(/\D+/g, '')

const isPhoneLike = (value: string): boolean =>
  /^[+\d\s()-]+$/.test(value) && digitsOf(value).length >= MIN_PHONE_DIGITS

const link = (kind: ContactKind, label: string, href: string, byPhone = false): ContactLink => ({
  kind,
  label,
  href,
  preferred: false,
  byPhone,
})

/**
 * The number in international form, as messengers need it. Local Ukrainian numbers (050…) get +38 —
 * most salons are in Ukraine; elsewhere only numbers already written with a country code are used.
 */
export function internationalDigits(phone: string, localCountry: string | null = UKRAINE): string | null {
  const trimmed = phone.trim()
  if (!isPhoneLike(trimmed)) return null
  const digits = digitsOf(trimmed)
  if (trimmed.startsWith('+')) return digits
  if (digits.startsWith('00')) return digits.slice(2)
  if (digits.startsWith('380') && digits.length === 12) return digits
  if (localCountry === UKRAINE && digits.startsWith('0') && digits.length === UKRAINE_LOCAL_DIGITS) return UKRAINE + digits
  return null
}

// Owners type contacts by hand. Only web links are passed through — never javascript: or other schemes.
function webUrl(value: string, hosts?: string[]): URL | null {
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(value) ? value : `https://${value}`
  try {
    const url = new URL(withScheme)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    if (!url.hostname.includes('.')) return null
    if (hosts && !hosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))) return null
    return url
  } catch {
    return null
  }
}

const handleFromUrl = (url: URL): string | null => url.pathname.split('/').find(Boolean) ?? null

export function phoneLink(value: string): ContactLink | null {
  if (!isPhoneLike(value)) return null
  const plus = value.trim().startsWith('+') ? '+' : ''
  return link('phone', value.trim(), `tel:${plus}${digitsOf(value)}`)
}

export function telegramLink(value: string, localCountry?: string | null): ContactLink | null {
  const trimmed = value.trim()
  if (isPhoneLike(trimmed)) {
    const digits = internationalDigits(trimmed, localCountry)
    return digits ? link('telegram', trimmed, `https://t.me/+${digits}`) : null
  }
  const url = /t(elegram)?\.me\//i.test(trimmed) ? webUrl(trimmed, ['t.me', 'telegram.me']) : null
  const handle = (url ? handleFromUrl(url) : HANDLE.exec(trimmed)?.[1])?.replace(/^@/, '')
  return handle ? link('telegram', `@${handle}`, `https://t.me/${handle}`) : null
}

export function viberLink(value: string, localCountry?: string | null): ContactLink | null {
  const digits = internationalDigits(value, localCountry)
  return digits ? link('viber', value.trim(), `viber://chat?number=%2B${digits}`) : null
}

export function whatsappLink(value: string, localCountry?: string | null): ContactLink | null {
  const trimmed = value.trim()
  if (isPhoneLike(trimmed)) {
    const digits = internationalDigits(trimmed, localCountry)
    return digits ? link('whatsapp', trimmed, `https://wa.me/${digits}`) : null
  }
  const url = webUrl(trimmed, ['wa.me', 'whatsapp.com'])
  return url ? link('whatsapp', 'WhatsApp', url.href) : null
}

/** A Facebook page opens as a Messenger chat with it; a numeric profile link just opens the profile. */
export function facebookLink(value: string): ContactLink | null {
  const trimmed = value.trim()
  const url = /facebook\.com|fb\.com|m\.me/i.test(trimmed) ? webUrl(trimmed, ['facebook.com', 'fb.com', 'm.me']) : null
  const handle = url ? handleFromUrl(url) : HANDLE.exec(trimmed)?.[1]
  if (url && (!handle || handle === 'profile.php' || handle === 'people')) {
    return link('facebook', 'Facebook', url.href)
  }
  return handle ? link('facebook', handle, `https://m.me/${handle}`) : null
}

export function emailLink(value: string): ContactLink | null {
  const trimmed = value.trim()
  if (!/^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/.test(trimmed)) return null
  return link('email', trimmed, `mailto:${trimmed}`)
}

export function instagramLink(value: string): ContactLink | null {
  const trimmed = value.trim()
  const url = /instagram\.com|instagr\.am/i.test(trimmed) ? webUrl(trimmed, ['instagram.com', 'instagr.am']) : null
  const handle = url ? handleFromUrl(url) : HANDLE.exec(trimmed)?.[1]
  return handle ? link('instagram', `@${handle}`, `https://instagram.com/${handle}`) : null
}

export function websiteLink(value: string): ContactLink | null {
  const url = webUrl(value.trim())
  return url ? link('website', url.hostname.replace(/^www\./, ''), url.href) : null
}

/** Viber, WhatsApp and Telegram chats with whoever owns the number. */
export function messengersForPhone(phone: string, localCountry?: string | null): ContactLink[] {
  return [viberLink(phone, localCountry), whatsappLink(phone, localCountry), telegramLink(phone, localCountry)]
    .filter((item): item is ContactLink => Boolean(item))
    .map((item) => ({ ...item, byPhone: true }))
}


const MESSENGER_ORDER: ContactKind[] = ['viber', 'telegram', 'whatsapp']

const present = (items: Array<ContactLink | null | '' | undefined>): ContactLink[] =>
  items.filter((item): item is ContactLink => Boolean(item))

const localCountryOf = (salon: Salon): string | null => (salon.currency && salon.currency !== 'UAH' ? null : UKRAINE)

/**
 * Every way to reach the salon, phones first. A messenger the salon did not fill in is offered by its
 * first phone number. Falls back to the phone and Instagram of an older backend.
 */
export function salonContactLinks(salon: Salon): ContactLink[] {
  const contacts = salon.contacts ?? {
    phones: salon.phone ? [salon.phone] : [],
    email: null,
    telegram: null,
    viber: null,
    whatsapp: null,
    instagram: salon.instagram,
    facebook: null,
    website: null,
    preferred: [],
  }
  const country = localCountryOf(salon)
  const preferred = new Set(contacts.preferred)

  const stated = present([
    contacts.telegram && telegramLink(contacts.telegram, country),
    contacts.viber && viberLink(contacts.viber, country),
    contacts.whatsapp && whatsappLink(contacts.whatsapp, country),
  ])
  const statedKinds = new Set(stated.map((item) => item.kind))
  const firstPhone = contacts.phones[0]
  const byPhone = firstPhone
    ? messengersForPhone(firstPhone, country).filter((item) => !statedKinds.has(item.kind))
    : []

  const links = [
    ...present(contacts.phones.map(phoneLink)),
    ...[...stated, ...byPhone].sort((a, b) => MESSENGER_ORDER.indexOf(a.kind) - MESSENGER_ORDER.indexOf(b.kind)),
    ...present([
      contacts.facebook && facebookLink(contacts.facebook),
      contacts.instagram && instagramLink(contacts.instagram),
      contacts.email && emailLink(contacts.email),
      contacts.website && websiteLink(contacts.website),
    ]),
  ]

  return links.map((item) => ({ ...item, preferred: preferred.has(item.kind) }))
}

/** The person who registered: their phone, the messengers on it and their email. */
export function ownerContactLinks(user: Owner, salon: Salon | null): ContactLink[] {
  const country = salon ? localCountryOf(salon) : UKRAINE
  return present([
    user.phone && phoneLink(user.phone),
    ...(user.phone ? messengersForPhone(user.phone, country) : []),
    emailLink(user.email),
  ])
}

/** Text the salons search looks through, so a pasted number or @handle finds the salon. */
export function contactSearchText(salon: Salon): string[] {
  const contacts = salon.contacts
  if (!contacts) return []
  return [...contacts.phones, ...contacts.phones.map(digitsOf), contacts.email, contacts.telegram, contacts.instagram].filter(
    (value): value is string => Boolean(value),
  )
}
